import { useEffect } from 'react'
import { Container, Typography, Box } from '@mui/material'
import Navbar from '../../components/nav/Navbar'
import Footer from '../../components/layout/Footer'

function PrivacyPolicy() {
    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'instant'
        })
    }, [])

    return (
        <>
            <Navbar />
            <Container component="main" maxWidth="md">
                <Box component="section" mt={4.2}>
                    <Typography component="h1" variant="h4" gutterBottom>
                        Privacy Policy
                    </Typography>

                    <Typography component="p" variant="subtitle1">
                        <strong>Effective Date:</strong> September 7th, 2026
                    </Typography>

                    <Typography component="p" variant="subtitle1">
                        Vibio ("we," "our," or "us") values your privacy. This Privacy Policy explains what personal
                        information we collect, how we use and share it, and the rights you have, whether you hold a
                        Vibio account (a "Creator") or simply visit a Creator's public Vibio page (a "Visitor"). It
                        applies to the Vibio platform, website, and related services (the "Service"). We do not sell
                        your personal data, and we do not share it with third parties for their marketing purposes.
                    </Typography>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            1. Information We Collect from Creators
                        </Typography>
                        <Typography component="p" variant="body1">
                            When you create and use a Vibio account, we collect:
                        </Typography>
                        <ul>
                            <Typography component="li" variant="body1">
                                <strong>Account information:</strong> your email address, username, and profile details you choose to add.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Sign-in with Google:</strong> if you register or sign in with Google, we
                                receive your name, email address, and profile picture from Google. We never receive
                                your Google password.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Content you publish:</strong> videos, profile images, links, and other material
                                you add to your page.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Onboarding information:</strong> answers you provide during onboarding, such as
                                what type of creator you are and how you heard about Vibio.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Billing information:</strong> When you subscribe to a paid plan, payments are
                                handled by our payment processor, Stripe. We share your email address and subscription
                                status with Stripe and receive confirmation of payment events in return. Vibio never
                                stores your full card details.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Usage and log data:</strong> technical information generated when you use the
                                Service, such as log records and device/browser information, used for security and
                                troubleshooting.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Communications:</strong> messages you send us, for example support requests to{' '}
                                <a href="mailto:info@vibio.co">info@vibio.co</a>.
                            </Typography>
                        </ul>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            2. Information We Collect from Visitors (Page Analytics)
                        </Typography>
                        <Typography component="p" variant="body1">
                            When you visit a Creator's public Vibio page, we collect a limited set of analytics data so
                            that the Creator can understand how their page performs:
                        </Typography>
                        <ul>
                            <Typography component="li" variant="body1">
                                Page views, approximate video watch time, and clicks on the page's links.
                            </Typography>
                            <Typography component="li" variant="body1">
                                The source of your visit (for example, the referring website or a source tag in the
                                link you followed).
                            </Typography>
                            <Typography component="li" variant="body1">
                                Your general device type (such as mobile or desktop), derived from your browser's
                                user-agent string.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Your approximate country, derived from your IP address. <strong>The IP address itself
                                    is used only for this lookup and is never stored.</strong>
                            </Typography>
                        </ul>
                        <Typography component="p" variant="body1">
                            This analytics data is designed to be privacy-preserving: we do not use advertising
                            cookies, we do not track you across other websites, and we do not build profiles of
                            Visitors. Visits are grouped using a randomly generated identifier that exists only for the
                            duration of your browsing session and is not linked to your identity. Creators see this
                            data only in aggregate form —
                            never information identifying an individual Visitor.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            3. How We Use Your Information
                        </Typography>
                        <ul>
                            <Typography component="li" variant="body1">
                                To provide, maintain, and improve the Service, including hosting Creators' pages and
                                processing uploaded videos for playback.
                            </Typography>
                            <Typography component="li" variant="body1">
                                To operate paid subscriptions, including billing, trials, renewals, and plan changes.
                            </Typography>
                            <Typography component="li" variant="body1">
                                To provide Creators with aggregate analytics about their pages, as described in
                                Section 2.
                            </Typography>
                            <Typography component="li" variant="body1">
                                To send service-related emails, such as a welcome email when you sign up and important
                                notices about your account, billing, or changes to the Service. We may send
                                marketing communications about product updates. You can opt out at any time by sending an email to <a href='mailto:info@vibio.co'>info@vibio.co</a>.
                            </Typography>
                            <Typography component="li" variant="body1">
                                To keep the Service secure, including detecting fraud, abuse, and automated (bot)
                                traffic.
                            </Typography>
                            <Typography component="li" variant="body1">
                                To understand how Vibio is discovered and used (for example, through onboarding survey
                                answers), so we can improve the product.
                            </Typography>
                        </ul>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            4. Legal Bases for Processing
                        </Typography>
                        <Typography component="p" variant="body1">
                            Where the EU/UK General Data Protection Regulation applies, we rely on the following legal
                            bases: <strong>performance of a contract</strong> (providing the Service you signed up for,
                            including billing); <strong>legitimate interests</strong> (first-party, privacy-preserving
                            page analytics, service security, fraud and bot prevention, and improving the Service);{' '}
                            <strong>consent</strong> (optional communications and any feature that asks for your
                            permission); and <strong>legal obligation</strong> (such as retaining billing records where
                            required by tax law).
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            5. How We Share Information
                        </Typography>
                        <Typography component="p" variant="body1">
                            We do not sell your personal data, and we do not share it with third parties for their
                            marketing purposes. We share data only with service providers that help us operate the
                            Service, only to the extent necessary:
                        </Typography>
                        <ul>
                            <Typography component="li" variant="body1">
                                <strong>Payment processing:</strong> Stripe, for subscription billing.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Authentication:</strong> Google, if you choose to sign in with Google.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Hosting, storage, and content delivery:</strong> cloud infrastructure and
                                content-delivery providers that host the Service and serve uploaded media (videos and
                                images) to viewers.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Email delivery:</strong> providers used to send the service emails described in
                                Section 3.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Geolocation:</strong> approximate country lookup is performed using a
                                geolocation database hosted on our own servers; Visitor IP addresses are not sent to
                                any third party for this purpose.
                            </Typography>
                        </ul>
                        <Typography component="p" variant="body1">
                            We may also disclose information if required by law, to protect the rights, safety, or
                            property of Vibio or others, or as part of a merger, acquisition, or sale of assets (in
                            which case this Privacy Policy will continue to apply to your data).
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            6. Content Visibility and Shared Reports
                        </Typography>
                        <Typography component="p" variant="body1">
                            Any videos, profile images, or links a Creator publishes on their Vibio page are publicly
                            visible by default. Please only publish content you are comfortable sharing publicly.
                            Creators may also generate shareable reports containing aggregate statistics about their
                            page; anyone with the link to such a report can view it. These reports contain only
                            aggregate figures and never identify individual Visitors.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            7. Browser Storage
                        </Typography>
                        <Typography component="p" variant="body1">
                            Like most web applications, Vibio stores small amounts of data in your browser that are
                            strictly necessary for the Service to function — for example, to keep you signed in, to
                            remember your progress through setup, and to avoid counting the same page visit twice. We
                            do not use advertising or cross-site tracking cookies.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            8. Data Retention
                        </Typography>
                        <ul>
                            <Typography component="li" variant="body1">
                                <strong>Account data and Content:</strong> retained for as long as your account exists.
                                When you delete your account, your profile, uploaded media, and personal information
                                are deleted, except for copies in routine backups (removed on a rolling basis) and
                                records we must keep to comply with legal obligations, such as billing records.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Analytics data:</strong> retained in aggregate to provide Creators with
                                historical statistics. As described in Section 2, it does not identify individual
                                Visitors.
                            </Typography>
                            <Typography component="li" variant="body1">
                                <strong>Visitor IP addresses:</strong> not retained — discarded after the country lookup.
                            </Typography>
                        </ul>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            9. Data Security
                        </Typography>
                        <Typography component="p" variant="body1">
                            We take reasonable technical and organizational measures to protect your personal data
                            against unauthorized access, alteration, disclosure, or destruction. However, no online platform can
                            guarantee absolute security. If we become aware of a breach affecting your personal data,
                            we will notify you and the relevant authorities where required by law.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            10. Your Rights
                        </Typography>
                        <Typography component="p" variant="body1">
                            You have the right to:
                        </Typography>
                        <ul>
                            <Typography component="li" variant="body1">
                                Access, correct, or delete your personal information.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Receive a copy of the data you provided in a portable format.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Object to, or request restriction of, certain processing, and withdraw consent at any
                                time where processing is based on consent.
                            </Typography>
                            <Typography component="li" variant="body1">
                                Request deletion of your account and associated data by emailing{' '}
                                <a href="mailto:info@vibio.co">info@vibio.co</a>.
                            </Typography>
                            <Typography component="li" variant="body1">
                                If you are located in the European Economic Area or the United Kingdom, lodge a
                                complaint with your local data protection authority.
                            </Typography>
                        </ul>
                        <Typography component="p" variant="body1">
                            If you are a California resident, you have equivalent rights under the California Consumer
                            Privacy Act (CCPA), including the rights to know, delete, and correct your personal
                            information and to be free from discrimination for exercising those rights. We do not
                            "sell" or "share" personal information as those terms are defined by the CCPA. We will
                            respond to all rights requests within the timeframes required by applicable law, and you
                            may exercise any of these rights by emailing{' '}
                            <a href="mailto:info@vibio.co">info@vibio.co</a>.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            11. Children's Privacy
                        </Typography>
                        <Typography component="p" variant="body1">
                            The Service is not directed at children, and you must be at least 16 years old to use it.
                            We do not knowingly collect personal data from anyone under 16. If you believe a child has
                            provided us with personal data, please contact us at{' '}
                            <a href="mailto:info@vibio.co">info@vibio.co</a> and we will delete it.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            12. Changes to this Privacy Policy
                        </Typography>
                        <Typography component="p" variant="body1">
                            We may update this Privacy Policy periodically. If we make significant changes, we will
                            notify you — for example by email or by a notice within the Service. Continued use of the Service after the updated policy takes effect constitutes
                            acceptance of the updated policy.
                        </Typography>
                    </Box>

                    <Box component="section" mt={3} mb={6}>
                        <Typography component="h2" variant="h5" gutterBottom>
                            13. Contact Us
                        </Typography>
                        <Typography component="p" variant="body1">
                            If you have questions or concerns regarding this Privacy Policy or how we handle your data,
                            please contact us at <a href="mailto:info@vibio.co">info@vibio.co</a>.
                        </Typography>
                    </Box>
                </Box>
            </Container>
            <Footer />
        </>
    )
}

export default PrivacyPolicy
